import React, { FC, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import axiosInstant from "../../../../api";
import { getProjectFileVersions } from "../../../../api/endpoints";
// eslint-disable-next-line import/no-cycle
import { VariationReviewModal } from "./VariationReviewModal";
import { getVideoThumbnail } from "../../../../utils/downloadHelpers";
import { DotLottleLoader } from "../../../../components/Loader";
import videoPlaceholder from "../../../../assets/video_placeholder.png";

interface PreviousVersion {
    id: number;
    version: number;
    length: number | null;
    aspect_ratio: string | null;
    createdAt: string;
    comment_count: number;
    location?: string;
    key?: string;
    cloudFrontDomain?: string;
}

interface Props {
    projectId: number;
    file: any;
    project?: any;
    initialVersions?: PreviousVersion[];
    onClose: () => void;
}

const formatDate = (value: string) =>
    new Date(value).toLocaleString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });

const formatLength = (seconds: number | null) => {
    if (!seconds) return null;
    const total = Math.round(seconds);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

export const PreviousVersionsModal: FC<Props> = ({
    projectId,
    file,
    project,
    initialVersions,
    onClose,
}) => {
    const [versions, setVersions] = useState<PreviousVersion[]>(initialVersions || []);
    const [loading, setLoading] = useState(!initialVersions);
    const [failed, setFailed] = useState(false);
    const [selected, setSelected] = useState<PreviousVersion | null>(null);

    useEffect(() => {
        if (initialVersions) return undefined;
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        axiosInstant
            .get(getProjectFileVersions(projectId, file.id))
            .then(({ data }) => {
                if (!cancelled) setVersions(data?.versions || []);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId, file.id, initialVersions]);

    if (selected) {
        return (
            <VariationReviewModal
                open
                readOnly
                title={`Version ${selected.version}`}
                project={project}
                variationFile={{ ...selected, projectId }}
                onClose={() => setSelected(null)}
            />
        );
    }

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px" } }}>
            <DialogTitle className="flex justify-between items-start gap-4 !pb-3">
                <span className="flex flex-col gap-1">
                    <Typography variant="h5">Previous Versions</Typography>
                    <Typography sx={{ fontSize: 12.5, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                        Earlier cuts of this deliverable, kept for reference. Opens view only.
                    </Typography>
                </span>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className="border-y border-border-token">
                {loading && (
                    <div className="flex justify-center py-10">
                        <DotLottleLoader fill={false} sx={{ width: 64, height: 64 }} />
                    </div>
                )}
                {!loading && (failed || versions.length === 0) && (
                    <Typography className="text-center py-10" sx={{ fontSize: 13, color: "var(--text-2)" }}>
                        {failed
                            ? "We could not load previous versions. Please try again."
                            : "No previous versions of this video."}
                    </Typography>
                )}
                <div className="flex flex-col gap-2 py-3">
                    {versions.map((version) => {
                        const duration = formatLength(version.length);
                        return (
                            <button
                                key={version.id}
                                type="button"
                                onClick={() => setSelected(version)}
                                className="group w-full text-left flex items-center gap-3 p-2.5 rounded-xl border border-border-token bg-surface hover:border-accent hover:bg-tint transition-colors"
                            >
                                <span className="relative shrink-0">
                                    <img
                                        src={getVideoThumbnail(version)}
                                        alt=""
                                        onError={(e) => {
                                            e.currentTarget.src = videoPlaceholder;
                                        }}
                                        className="w-[92px] h-[54px] rounded-lg object-cover bg-surface-2"
                                    />
                                    <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <PlayArrowRoundedIcon sx={{ fontSize: 26, color: "#fff" }} />
                                    </span>
                                    {duration && (
                                        <span className="absolute bottom-1 right-1 px-1.5 rounded bg-black/70 text-white text-[10px] font-semibold leading-[15px]">
                                            {duration}
                                        </span>
                                    )}
                                </span>

                                <span className="flex flex-col gap-0.5 min-w-0 flex-1">
                                    <span className="flex items-center gap-2">
                                        <span className="text-[13.5px] font-bold text-text-1">
                                            Version {version.version}
                                        </span>
                                        {version.aspect_ratio && (
                                            <span className="px-1.5 py-[1px] rounded-md bg-surface-2 text-[10.5px] font-semibold text-text-2">
                                                {version.aspect_ratio}
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-[12px] text-text-2">{formatDate(version.createdAt)}</span>
                                </span>

                                {version.comment_count > 0 && (
                                    <span className="flex items-center gap-1 shrink-0 px-2 py-1 rounded-lg bg-tint text-[11.5px] font-semibold text-accent-text">
                                        <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />
                                        {version.comment_count}
                                    </span>
                                )}
                                <ChevronRightIcon className="shrink-0" sx={{ fontSize: 20, color: "var(--muted)" }} />
                            </button>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
};
